// Angular
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";

// Afidium
import { WORDING } from "../config/wording";
import { ON_PROCESSING_ALL_TASKS, ON_PROCESSING, IOnProcessingAllTasks, IOnProcessing } from "../config/init";
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { ITask, TaskStatus } from "libraries/service/resources/booking-task-resource/ITask";
import { ProcessingResourceService } from "app_sasha/shared/resources/processings/processingResource.service";
import { ILabels } from "libraries/interfaces/ILabels";
import { IPageChange, IPaginationRecordset } from "libraries/component/pagination/IPagination";

@Component({
    selector: 'processing-task-list',
    template: `
        <card>
            <div class="card-header-content">
                {{wording.bookingList}}
                <ng-container *ngIf="showIfStatusTaskError()">
                    <btn permissionShow="sasha.processing.email.use"
                        [label]="wording.resendAllTask"
                        type="actionHeaderLink"
                        (onClick)="recallAll()"
                    ></btn>
                </ng-container>

            </div>
            <div class="card-body-content">
                <notification *ngIf="notification.active"
                            [type]="notification.type">{{ notification.message }}</notification>
                <div class="list">
                    <div class="list-row" *ngFor="let task of sortedTasks; trackBy: getId">
                        <div>
                            <processing-task *ngIf="!checkIfTaskIsSending(task.id)"
                                             [processingId]="processingId"
                                             [task]="task"
                                             [processingType]="processingType"
                                             (onRetry)="recallOne($event);"></processing-task>
                            <loader *ngIf="checkIfTaskIsSending(task.id)" [message]="wording.recallTask"></loader>
                        </div>
                    </div>
                    <pagination *ngIf="recordset" [recordset]="recordset" [listLength]="recordset.nbrecord" (onPageChange)="pageChange($event)"></pagination>
                </div>
                <loader *ngIf="onProcessingTask.active" [message]="onProcessingTask.message"></loader>
            </div>
        </card>
    `,
})
export class ProcessingTaskListComponent implements OnChanges {
    @Input()
    public tasks: ITask[];

    @Input()
    public processingId: number;

    @Input()
    public processingType: string;

    @Input()
    public recordset: IPaginationRecordset = {total: 500, first: 1};

    @Output()
    public readonly onRetry: EventEmitter<void> = new EventEmitter<void>();
    @Output()
    public readonly onPageChange: EventEmitter<IPageChange> = new EventEmitter<IPageChange>();

    public sortedTasks: ITask[];
    public wording: ILabels = WORDING;
    public onProcessing: IOnProcessing = ON_PROCESSING;
    public onProcessingTask: IOnProcessingAllTasks = ON_PROCESSING_ALL_TASKS;
    public notification: INotification = {
        active: false,
        message: null,
        type: NotificationType.Error,
    };

    constructor(
        private readonly processingResourceService: ProcessingResourceService,
    ) { }

    public ngOnChanges(changes: SimpleChanges): void {
        if(changes.tasks){
            if (changes.tasks.currentValue !== changes.tasks.previousValue) {
                this.sortedTasks = this.tasks.reverse();
            }
        }
    }

    public getId(task: ITask): number {
        return task.id;
    }

    public showIfStatusTaskError(): boolean {
        return this.tasks.filter((task: ITask) => task.status === TaskStatus.Error || task.status === TaskStatus.Warning).length > 0;
    }

    public recallAll(): void {
        this.onProcessingTask = {
            active: true,
            message: this.wording.sending
        };

        this.notification.active = false;

        const taskIds: number[] = this.tasks
            .filter((task: ITask) => task.status === TaskStatus.Error || task.status === TaskStatus.Warning)
            .map((task: ITask) => task.id);

        this.processingResourceService.relaunchTasks(this.processingId, taskIds)
            .subscribe(
                (tasksDetails: ITask[]) => {
                    this.tasks = tasksDetails;
                    this.onRetry.emit();
                },
                (error: Error) => {
                    this.notification = {
                        type: NotificationType.Error,
                        message: error.message,
                        active: true
                    };

                    this.onProcessingTask = ON_PROCESSING_ALL_TASKS;
                },
                () => this.onProcessingTask = ON_PROCESSING_ALL_TASKS
            );
    }

    public checkIfTaskIsSending(taskId: number): boolean {
        return this.onProcessing.id.indexOf(taskId) > -1;
    }

    public recallOne(task: ITask): void {
        this.onProcessing = {
            active: false,
            id: [...this.onProcessing.id, task.id]
        };

        const modifiedTask: ITask = {
            ...task,
            status: TaskStatus.Pending,
            message: undefined,
        };
        // This improves the display just after relaunching the task
        // it is already shown as pending
        this.sortedTasks = this.sortedTasks.map((sortedTask: ITask) => sortedTask.id === task.id ? modifiedTask : sortedTask);

        this.processingResourceService.relaunchTasks(this.processingId, [task.id]).subscribe(
            (taskDetails: ITask[]) => {
                const [updatedTask]: ITask[] = taskDetails;

                this.tasks = this.tasks.map((taskItem: ITask) =>
                    (taskItem.id === updatedTask.id ?
                        {
                            message: updatedTask.message,
                            whenLastUpdated: updatedTask.whenLastUpdated,
                            status: updatedTask.status,
                            id: updatedTask.id,
                            booking: updatedTask.booking
                        } :
                        taskItem)
                );

                this.onRetry.emit();
            },
            (error: Error) => this.onProcessing = ON_PROCESSING,
            () => this.onProcessing = ON_PROCESSING
        );
    }

    public pageChange(change: IPageChange): void {
        this.onPageChange.emit(change);
    }
}
